import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class TelemetryService {
    private appInsights: ApplicationInsights;
    private angularPlugin: AngularPlugin;

    constructor(private router: Router) {
        this.angularPlugin = new AngularPlugin();

        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: environment.appInsights.connectionString,
                extensions: [this.angularPlugin],
                extensionConfig: {
                    [this.angularPlugin.identifier]: {
                        router: this.router,
                        enablePageViewTracking: true
                    }
                },
                disableFetchTracking: false,
                disableCorrelationHeaders: false,
                correlationHeaderExcludedDomains: [
                    'azurewebsites.net',
                    '*.azurewebsites.net'
                ],
                samplingPercentage: 100,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                enableUnhandledPromiseRejectionTracking: true,
                enableAutoRouteTracking: false
            }
        });
    }

    init(): void {
        this.appInsights.loadAppInsights();

        this.appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.baseType === 'PageviewData' && envelope.baseData) {
                envelope.baseData['properties'] = envelope.baseData['properties'] || {};

                // Add full URL with query params and hash
                const fullUrl = window.location.href;
                const pathname = window.location.pathname;
                const search = window.location.search;
                const hash = window.location.hash;

                // Update page view name without sensitive query params (like orderId)
                envelope.baseData['name'] = pathname + hash;
                envelope.baseData['uri'] = pathname + hash;

                // Add query parameters as individual properties
                if (search) {
                    const urlParams = new URLSearchParams(search);
                    const queryParamsObj: Record<string, string> = {};
                    urlParams.forEach((value, key) => {
                        queryParamsObj[key] = value;
                        // Also add with prefix for easy filtering
                        envelope.baseData!['properties'][`queryParam_${key}`] = value;
                    });
                    envelope.baseData['properties']['queryParams'] = JSON.stringify(queryParamsObj);
                }

                // Add route params from Angular router state
                const routeParams = this.getCurrentRouteParams();
                if (Object.keys(routeParams).length > 0) {
                    envelope.baseData['properties']['routeParams'] = JSON.stringify(routeParams);
                    // Add individual route params
                    Object.keys(routeParams).forEach(key => {
                        envelope.baseData!['properties'][`routeParam_${key}`] = routeParams[key];
                    });
                }

                // Add referrer information
                if (document.referrer) {
                    envelope.baseData['properties']['referrer'] = document.referrer;
                    envelope.baseData['properties']['referrerDomain'] = this.extractDomain(document.referrer);
                }

                // Add UTM parameters
                const urlParams = new URLSearchParams(window.location.search);
                const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
                utmParams.forEach(param => {
                    const value = urlParams.get(param);
                    if (value) {
                        envelope.baseData!['properties'][param] = value;
                    }
                });
            }

            return true;
        });
    }

    /**
     * Get current route parameters from Angular router
     */
    private getCurrentRouteParams(): Record<string, string> {
        const routeParams: Record<string, string> = {};
        let route = this.router.routerState.root;

        while (route) {
            if (route.snapshot.params) {
                Object.assign(routeParams, route.snapshot.params);
            }
            route = route.firstChild!;
        }

        return routeParams;
    }

    private extractDomain(url: string): string {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname;
        } catch {
            return url;
        }
    }

    trackFormStep(formId: string, step: string, properties?: Record<string, any>): void {
        this.appInsights.trackEvent(
            { name: 'FormStep' },
            { formId, step, ...properties }
        );
    }

    trackPaymentStep(step: string, orderId?: string, amount?: number, properties?: Record<string, any>): void {
        this.appInsights.trackEvent(
            { name: 'PaymentStep' },
            { step, orderId, amount, ...properties }
        );
    }

    trackCustomEvent(name: string, properties?: Record<string, any>): void {
        this.appInsights.trackEvent({ name }, properties);
    }

    trackError(error: Error, properties?: Record<string, any>): void {
        this.appInsights.trackException({ exception: error }, properties);
    }

    trackMetric(name: string, value: number, properties?: Record<string, any>): void {
        this.appInsights.trackMetric({ name, average: value }, properties);
    }

    trackUserAction(action: string, properties?: Record<string, any>): void {
        this.appInsights.trackEvent({ name: 'UserAction' }, { action, ...properties });
    }

    setAuthenticatedUserContext(userId: string, accountId?: string): void {
        this.appInsights.setAuthenticatedUserContext(userId, accountId);
    }

    clearAuthenticatedUserContext(): void {
        this.appInsights.clearAuthenticatedUserContext();
    }

    trackDependency(id: string, name: string, url: string, duration: number, success: boolean): void {
        this.appInsights.trackDependencyData({
            id,
            name,
            target: url,
            duration,
            success,
            responseCode: success ? 200 : 500
        });
    }
}
